import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Participant {
  id: string;
  user_id: string;
  player1_name: string;
  player2_name: string | null;
  registered_at: string;
  group: string | null;
  subcategory: string | null;
  category: string | null;
}

export function useSubcategoryParticipants(
  eventId: string,
  userSubcategory: string | null,
  userCategory: string | null
) {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    async function fetchParticipants() {
      try {
        setLoading(true);

        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        setCurrentUserId(user?.id || null);

        console.log('[useSubcategoryParticipants] Params:', {
          eventId,
          userSubcategory,
          userCategory,
          subcategoryType: typeof userSubcategory,
          willFetch: !!(eventId && userCategory)
        });

        if (!eventId || !userCategory) {
          console.log('[useSubcategoryParticipants] Missing required params, skipping fetch');
          setParticipants([]);
          setLoading(false);
          return;
        }

        // Build query with conditional subcategory filter
        let query = supabase
          .from('registrations')
          .select('*')
          .eq('event_id', eventId)
          .eq('status', 'confirmed');

        if (userCategory) {
          query = query.eq('category', userCategory);
        }

        // Only filter by subcategory if it exists and is not empty
        if (userSubcategory && userSubcategory.trim()) {
          query = query.eq('subcategory', userSubcategory);
        }

        const { data, error } = await query.order('registered_at', { ascending: true });

        if (error) throw error;

        console.log('[useSubcategoryParticipants] Results:', {
          count: data?.length || 0,
          participants: data?.map(p => ({
            player1: p.player1_name,
            player2: p.player2_name,
            subcategory: p.subcategory
          }))
        });

        setParticipants(data || []);
      } catch (err) {
        console.error('Error fetching subcategory participants:', err);
        setParticipants([]);
      } finally {
        setLoading(false);
      }
    }

    fetchParticipants();
  }, [eventId, userSubcategory, userCategory]);

  return {
    participants,
    loading,
    currentUserId,
    totalCount: participants.length
  };
}
